export class GameSimulation {
  constructor(gameInterface, users, width, height) {
    this.gameInterface = gameInterface;
    this.isGameOver = false;
    this.playerIds = Object.keys(users);
    this.playerCount = this.playerIds.length;
    
    // Game configuration
    this.bombStartSize = 30;       // Starting size in pixels
    this.bombExplodeSize = this.bombStartSize * 3; // Size at which bomb explodes
    this.bombMinLifetime = 5000;   // Minimum bomb lifetime in ms (5 seconds)
    this.bombMaxLifetime = 15000;  // Maximum bomb lifetime in ms (15 seconds)
    this.passCooldown = 1250;      // 1.25 seconds in ms (updated from 750ms)
    this.fakeCooldown = 250;       // 0.25 seconds in ms
    this.passAnimationTime = 300;  // Time for pass animation in ms
    this.resetDelay = 2000;        // Delay before resetting bomb after explosion
    this.resetTimer = 0;           // Timer for resetting bomb
    this.fooledDuration = 500;     // Duration in ms that players are fooled by fake passes
    
    // Calculate positions in a circle
    const radius = Math.min(width, height) * 0.35;
    const centerX = width / 2;
    const centerY = height / 2;
    const positions = [];
    
    for (let i = 0; i < this.playerCount; i++) {
      const angle = (i / this.playerCount) * Math.PI * 2 - Math.PI / 2;
      positions.push({
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
        angle: angle
      });
    }
    
    this.gameState = {
      entities: {},
      bomb: {
        id: 'bomb',
        holderId: this.playerIds[Math.floor(Math.random() * this.playerCount)],
        size: this.bombStartSize,
        growthRate: this.calculateGrowthRate(),
        isExploding: false,
        isAnimating: false,
        fromPosition: null,
        toPosition: null,
        animationProgress: 0,
        isFakePass: false,
        bombStartSize: this.bombStartSize,
        bombExplodeSize: this.bombExplodeSize
      },
      activePlayers: [...this.playerIds],
      eliminatedPlayers: [],
      screenSize: {
        width,
        height
      },
      isGameOver: this.isGameOver,
      winner: null,
      results: null,
      centerPosition: {
        x: centerX,
        y: centerY
      }
    };
    
    // Create player entities
    this.playerIds.forEach((userId, index) => {
      const position = positions[index];
      this.gameState.entities[userId] = {
        id: userId,
        name: users[userId].name,
        x: position.x,
        y: position.y,
        angle: position.angle,
        index: index,
        state: 'idle',
        isEliminated: false,
        hasBomb: userId === this.gameState.bomb.holderId,
        passCooldown: 0,
        fakeCooldown: 0,
        isLaughing: false,
        laughTime: 0,
        isFooledByFakePass: false,
        fooledTimeRemaining: 0
      };
    });
    
    // Handle pass bomb event
    this.gameInterface.on('pass_bomb', (data) => {
      if (this.isGameOver) return;
      
      const entity = this.gameState.entities[data.userId];
      const bomb = this.gameState.bomb;
      
      if (entity && !entity.isEliminated && entity.passCooldown <= 0) {
        // Apply cooldown regardless of whether player has the bomb
        entity.passCooldown = this.passCooldown;
        
        // Only actually pass the bomb if the player has it and conditions are right
        if (bomb.holderId === data.userId && !bomb.isAnimating && !bomb.isExploding) {
          this.passBomb(data.userId, false);
        }
      }
    });
    
    // Handle fake pass event
    this.gameInterface.on('fake_pass', (data) => {
      if (this.isGameOver) return;
      
      const entity = this.gameState.entities[data.userId];
      const bomb = this.gameState.bomb;
      
      if (entity && !entity.isEliminated && entity.fakeCooldown <= 0) {
        // Apply cooldown regardless of whether player has the bomb
        entity.fakeCooldown = this.fakeCooldown;
        
        // Only actually fake pass the bomb if the player has it and conditions are right
        if (bomb.holderId === data.userId && !bomb.isAnimating && !bomb.isExploding) {
          this.passBomb(data.userId, true);
        }
      }
    });
  }
  
  calculateGrowthRate() {
    // Calculate a random lifetime between min and max
    const lifetime = this.bombMinLifetime + Math.random() * (this.bombMaxLifetime - this.bombMinLifetime);
    
    // Calculate how much the bomb needs to grow in total
    const totalGrowth = this.bombExplodeSize - this.bombStartSize;
    
    // Calculate growth rate per millisecond
    const growthRatePerMs = totalGrowth / lifetime;
    
    // Convert to growth per frame (assuming 16ms per frame at 60fps)
    return growthRatePerMs * 16;
  }
  
  getNextPlayerIndex(currentIndex) {
    const activePlayers = this.gameState.activePlayers;
    if (activePlayers.length <= 1) return -1;
    
    // Find the current player's position in active players
    const currentActiveIndex = activePlayers.indexOf(this.playerIds[currentIndex]);
    if (currentActiveIndex === -1) return -1;
    
    // Get the next active player
    const nextActiveIndex = (currentActiveIndex + 1) % activePlayers.length;
    const nextPlayerId = activePlayers[nextActiveIndex];
    
    // Return the original index of this player
    return this.playerIds.indexOf(nextPlayerId);
  }
  
  passBomb(currentHolderId, isFake) {
    const bomb = this.gameState.bomb;
    const currentHolder = this.gameState.entities[currentHolderId];
    
    if (!currentHolder) return;
    
    const currentIndex = currentHolder.index;
    const nextIndex = this.getNextPlayerIndex(currentIndex);
    
    if (nextIndex === -1) return;
    
    const nextPlayerId = this.playerIds[nextIndex];
    const nextHolder = this.gameState.entities[nextPlayerId];
    
    if (!nextHolder) return;
    
    // Set up animation
    bomb.isAnimating = true;
    bomb.fromPosition = { x: currentHolder.x, y: currentHolder.y };
    bomb.toPosition = { x: nextHolder.x, y: nextHolder.y };
    bomb.animationProgress = 0;
    bomb.isFakePass = isFake;
    
    // If it's a real pass, update the holder after animation
    if (!isFake) {
      currentHolder.hasBomb = false;
      // The holder will be updated when animation completes
    } else {
      // For fake passes, mark the target player as "fooled"
      // This will be cleared after the fooledDuration expires
      nextHolder.isFooledByFakePass = true;
      nextHolder.fooledTimeRemaining = this.fooledDuration;
    }
  }
  
  explodeBomb() {
    const bomb = this.gameState.bomb;
    const holderId = bomb.holderId;
    const holder = this.gameState.entities[holderId];
    
    if (!holder) return;
    
    bomb.isExploding = true;
    holder.isEliminated = true;
    
    // Remove player from active list and add to eliminated list
    const activeIndex = this.gameState.activePlayers.indexOf(holderId);
    if (activeIndex !== -1) {
      this.gameState.activePlayers.splice(activeIndex, 1);
      this.gameState.eliminatedPlayers.push(holderId);
    }
    
    // Make other players laugh
    Object.values(this.gameState.entities).forEach(entity => {
      if (!entity.isEliminated && entity.id !== holderId) {
        entity.isLaughing = true;
        entity.laughTime = 2000; // Laugh for 2 seconds
      }
    });
    
    // Check if game is over
    if (this.gameState.activePlayers.length <= 1) {
      this.isGameOver = true;
      this.gameState.isGameOver = true;
      this.gameState.winner = this.gameState.activePlayers[0] || null;
      this.gameState.results = this.getResults();
    } else {
      // Set timer for resetting bomb
      this.resetTimer = this.resetDelay;
    }
  }
  
  resetBomb() {
    if (this.isGameOver) return;
    
    const bomb = this.gameState.bomb;
    bomb.size = this.bombStartSize;
    bomb.growthRate = this.calculateGrowthRate();
    bomb.isExploding = false;
    
    // Give bomb to next active player after the eliminated player
    if (this.gameState.activePlayers.length > 0) {
      // Find the last eliminated player
      const lastEliminatedId = this.gameState.eliminatedPlayers[this.gameState.eliminatedPlayers.length - 1];
      const lastEliminatedEntity = this.gameState.entities[lastEliminatedId];
      
      if (lastEliminatedEntity) {
        // Find the next player after the eliminated one in the original circle
        const eliminatedIndex = lastEliminatedEntity.index;
        let nextIndex = (eliminatedIndex + 1) % this.playerCount;
        
        // Find the next active player in the circle
        let nextPlayerId = this.playerIds[nextIndex];
        while (this.gameState.activePlayers.indexOf(nextPlayerId) === -1) {
          nextIndex = (nextIndex + 1) % this.playerCount;
          nextPlayerId = this.playerIds[nextIndex];
        }
        
        // Set the bomb holder to this next player
        bomb.holderId = nextPlayerId;
        
        // Update player states
        Object.values(this.gameState.entities).forEach(entity => {
          entity.hasBomb = entity.id === nextPlayerId;
        });
      } else {
        // Fallback to first active player if we can't find the last eliminated player
        const nextHolderId = this.gameState.activePlayers[0];
        bomb.holderId = nextHolderId;
        
        // Update player states
        Object.values(this.gameState.entities).forEach(entity => {
          entity.hasBomb = entity.id === nextHolderId;
        });
      }
    }
  }
  
  getResults() {
    const winnerName = this.gameState.winner ? 
      (this.gameState.entities[this.gameState.winner]?.name || "Unknown") : 
      "No winner";
    
    return {
      winner: winnerName,
      eliminated: this.gameState.eliminatedPlayers.map(id => this.gameState.entities[id]?.name || "Unknown")
    };
  }
  
  update(deltaTime) {
    if (this.isGameOver) return;
    
    const bomb = this.gameState.bomb;
    
    // Update cooldowns and fooled state
    Object.values(this.gameState.entities).forEach(entity => {
      if (entity.passCooldown > 0) {
        entity.passCooldown = Math.max(0, entity.passCooldown - deltaTime);
      }
      if (entity.fakeCooldown > 0) {
        entity.fakeCooldown = Math.max(0, entity.fakeCooldown - deltaTime);
      }
      if (entity.isLaughing && entity.laughTime > 0) {
        entity.laughTime = Math.max(0, entity.laughTime - deltaTime);
        if (entity.laughTime === 0) {
          entity.isLaughing = false;
        }
      }
      
      // Update fooled state timer
      if (entity.isFooledByFakePass) {
        entity.fooledTimeRemaining = Math.max(0, entity.fooledTimeRemaining - deltaTime);
        if (entity.fooledTimeRemaining === 0) {
          entity.isFooledByFakePass = false;
        }
      }
    });
    
    // Handle reset timer
    if (this.resetTimer > 0) {
      this.resetTimer -= deltaTime;
      if (this.resetTimer <= 0) {
        this.resetBomb();
      }
    }
    
    // Handle bomb animation
    if (bomb.isAnimating) {
      bomb.animationProgress += deltaTime / this.passAnimationTime;
      
      if (bomb.animationProgress >= 1) {
        bomb.isAnimating = false;
        bomb.animationProgress = 0;
        
        // If it was a real pass, update the holder
        if (!bomb.isFakePass) {
          const nextIndex = this.getNextPlayerIndex(this.gameState.entities[bomb.holderId].index);
          if (nextIndex !== -1) {
            const nextPlayerId = this.playerIds[nextIndex];
            bomb.holderId = nextPlayerId;
            this.gameState.entities[nextPlayerId].hasBomb = true;
          }
        }
      }
    } 
    // Grow bomb if not exploding or animating
    else if (!bomb.isExploding && this.resetTimer <= 0) {
      bomb.size += bomb.growthRate * (deltaTime / 16); // Normalize to ~60fps
      
      // Check if bomb should explode
      if (bomb.size >= this.bombExplodeSize) {
        this.explodeBomb();
      }
    }
    
    // Update game state if game is over
    if (this.gameState.activePlayers.length <= 1 && !this.isGameOver) {
      this.isGameOver = true;
      this.gameState.isGameOver = true;
      this.gameState.winner = this.gameState.activePlayers[0] || null;
      this.gameState.results = this.getResults();
    }
  }
}
